import React, { useState, useEffect, useCallback } from 'react';
import {
  X,
  TrendingUp,
  TrendingDown,
  Minus,
  BarChart3,
  DollarSign,
  Users,
  Clock,
  CheckCircle,
  XCircle,
  Info,
} from 'lucide-react';
import api from '../services/api';
import { Ticker } from '../services/tickerService';
import schwabService, { SchwabQuote } from '../services/schwabService';

export interface TickerAnalysisSummary {
  symbol: string;
  lastUpdated: Date;
  technical?: {
    latest: any | null;
    timeframes: {
      [timeframe: string]: {
        result: any | null;
        status: 'available' | 'pending' | 'failed' | 'not_available';
        lastAnalysisDate?: Date;
      };
    };
    status: 'available' | 'pending' | 'failed' | 'not_available';
    lastAnalysisDate?: Date;
  };
  fundamental?: {
    latest: any | null;
    status: 'available' | 'pending' | 'failed' | 'not_available';
    lastAnalysisDate?: Date;
  };
  social?: {
    latest: any | null;
    status: 'available' | 'pending' | 'failed' | 'not_available';
    lastAnalysisDate?: Date;
  };
  sentiment?: {
    latest: any | null;
    status: 'available' | 'pending' | 'failed' | 'not_available';
    lastAnalysisDate?: Date;
  };
  articleMentions?: {
    totalMentions: number;
    averageSentiment: string;
    averageConfidence: number;
    recentTrend: 'increasing' | 'decreasing' | 'stable';
    lastMentionDate?: Date;
  };
  overallScore?: number;
  overallRecommendation?:
    | 'BUY'
    | 'SELL'
    | 'HOLD'
    | 'STRONG_BUY'
    | 'STRONG_SELL';
  confidence?: number;
  marketData?: {
    currentPrice: number;
    // Add other market data fields if needed
  };
}

interface TickerAnalysisSummaryModalProps {
  ticker: Ticker | null;
  isOpen: boolean;
  onClose: () => void;
}

const TickerAnalysisSummaryModal: React.FC<TickerAnalysisSummaryModalProps> = ({
  ticker,
  isOpen,
  onClose,
}) => {
  const [summary, setSummary] = useState<TickerAnalysisSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [articleMentions, setArticleMentions] = useState<any[]>([]);
  const [showArticleMentions, setShowArticleMentions] = useState(false);
  const [schwabQuote, setSchwabQuote] = useState<SchwabQuote | null>(null);
  const [schwabQuoteLoading, setSchwabQuoteLoading] = useState(false);
  const [schwabQuoteError, setSchwabQuoteError] = useState<string | null>(null);

  const loadAnalysisSummary = useCallback(async () => {
    if (!ticker) return;

    setLoading(true);
    setError(null);

    try {
      const summaryData = await api.getTickerAnalysisSummary(
        ticker.symbol,
        true,
      );
      setSummary(summaryData);

      // Load article mentions if available
      if (summaryData.articleMentions?.totalMentions > 0) {
        const mentions = await api.getTickerArticleMentions(ticker.symbol, 10);
        setArticleMentions(mentions.mentions || []);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load analysis summary');
    } finally {
      setLoading(false);
    }
  }, [ticker]);

  const fetchSchwabQuote = useCallback(async () => {
    if (!ticker) return;
    setSchwabQuote(null);
    setSchwabQuoteError(null);
    setSchwabQuoteLoading(true);
    try {
      const quote = await schwabService.getQuote(ticker.symbol);
      setSchwabQuote(quote);
    } catch (err: any) {
      setSchwabQuoteError(err.message || 'Failed to fetch Schwab quote');
    } finally {
      setSchwabQuoteLoading(false);
    }
  }, [ticker]);

  useEffect(() => {
    if (isOpen && ticker) {
      loadAnalysisSummary();
      fetchSchwabQuote();
    }
  }, [isOpen, ticker, loadAnalysisSummary, fetchSchwabQuote]);

  const getRecommendationColor = (recommendation: string): string => {
    switch (recommendation) {
      case 'STRONG_BUY':
        return '#10b981'; // green-500
      case 'BUY':
        return '#34d399'; // green-400
      case 'HOLD':
        return '#f59e0b'; // amber-500
      case 'SELL':
        return '#f87171'; // red-400
      case 'STRONG_SELL':
        return '#ef4444'; // red-500
      default:
        return '#6b7280'; // gray-500
    }
  };

  const getSentimentColor = (sentiment: string): string => {
    switch (sentiment) {
      case 'bullish':
        return '#10b981'; // green-500
      case 'bearish':
        return '#ef4444'; // red-500
      case 'neutral':
        return '#6b7280'; // gray-500
      default:
        return '#6b7280'; // gray-500
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'available':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'pending':
        return <Clock className="h-4 w-4 text-amber-500" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'not_available':
        return <Info className="h-4 w-4 text-gray-500" />;
      default:
        return <Info className="h-4 w-4 text-gray-500" />;
    }
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'bullish':
        return <TrendingUp className="h-4 w-4 text-green-500" />;
      case 'bearish':
        return <TrendingDown className="h-4 w-4 text-red-500" />;
      case 'neutral':
        return <Minus className="h-4 w-4 text-gray-500" />;
      default:
        return <Minus className="h-4 w-4 text-gray-500" />;
    }
  };

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!isOpen || !ticker) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-6xl shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h3 className="text-2xl font-bold text-gray-900">
              Analysis Summary: {ticker.symbol}
            </h3>
            <p className="text-gray-600">{ticker.name}</p>
            {/* Schwab Quote Section */}
            {schwabQuoteLoading ? (
              <p className="text-blue-600 text-sm mt-1">
                Loading latest price...
              </p>
            ) : schwabQuoteError ? (
              <p className="text-red-600 text-sm mt-1">{schwabQuoteError}</p>
            ) : schwabQuote ? (
              <div className="mt-1 text-sm">
                <span className="font-semibold text-blue-700 text-lg">
                  ${schwabQuote.lastPrice?.toFixed(2) ?? 'N/A'}
                </span>
                <span className="ml-2 text-gray-600">Last</span>
                <span className="ml-4">
                  <span className="font-medium">Bid:</span> $
                  {schwabQuote.bidPrice?.toFixed(2) ?? 'N/A'}
                </span>
                <span className="ml-4">
                  <span className="font-medium">Ask:</span> $
                  {schwabQuote.askPrice?.toFixed(2) ?? 'N/A'}
                </span>
                <span className="ml-4">
                  <span className="font-medium">Volume:</span>{' '}
                  {schwabQuote.totalVolume?.toLocaleString() ?? 'N/A'}
                </span>
              </div>
            ) : (
              <p className="text-gray-500 text-sm mt-1">
                No price data available
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : error ? (
          <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
            <h4 className="text-lg font-semibold text-red-800 mb-2">Error</h4>
            <p className="text-red-600">{error}</p>
            <button
              onClick={loadAnalysisSummary}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            >
              Retry
            </button>
          </div>
        ) : summary ? (
          <div className="space-y-6">
            {/* Overall Summary */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-xl font-semibold text-gray-900">
                  Overall Analysis
                </h4>
                <div className="flex items-center space-x-4">
                  {summary.overallRecommendation && (
                    <span
                      className="px-3 py-1 rounded-full text-sm font-medium"
                      style={{
                        backgroundColor:
                          getRecommendationColor(
                            summary.overallRecommendation,
                          ) + '20',
                        color: getRecommendationColor(
                          summary.overallRecommendation,
                        ),
                      }}
                    >
                      {summary.overallRecommendation}
                    </span>
                  )}
                  {summary.overallScore && (
                    <span className="text-lg font-bold text-gray-900">
                      {summary.overallScore}/100
                    </span>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="font-medium">Confidence:</span>{' '}
                  {summary.confidence ? `${summary.confidence}%` : 'N/A'}
                </div>
                <div>
                  <span className="font-medium">Last Updated:</span>{' '}
                  {formatDate(summary.lastUpdated)}
                </div>
                <div>
                  <span className="font-medium">Technical:</span>{' '}
                  {summary.technical?.status || 'N/A'}
                </div>
                <div>
                  <span className="font-medium">Fundamental:</span>{' '}
                  {summary.fundamental?.status || 'N/A'}
                </div>
              </div>
            </div>

            {/* Analysis Sections */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Technical Analysis */}
              <div className="bg-white p-6 rounded-lg border">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    <BarChart3 className="h-5 w-5 text-blue-600" />
                    <h4 className="text-lg font-semibold">
                      Technical Analysis
                    </h4>
                  </div>
                  {getStatusIcon(summary.technical?.status || 'not_available')}
                </div>

                {summary.technical?.latest ? (
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span>Status:</span>
                      <span className="font-medium capitalize">
                        {summary.technical.status}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Last Analysis:</span>
                      <span>
                        {summary.technical.lastAnalysisDate
                          ? formatDate(summary.technical.lastAnalysisDate)
                          : 'N/A'}
                      </span>
                    </div>
                    {summary.technical.latest.data?.summary && (
                      <>
                        <div className="flex justify-between text-sm">
                          <span>Trend:</span>
                          <div className="flex items-center space-x-1">
                            {getTrendIcon(
                              summary.technical.latest.data.summary.trend,
                            )}
                            <span className="font-medium capitalize">
                              {summary.technical.latest.data.summary.trend}
                            </span>
                          </div>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span>Strength:</span>
                          <span className="font-medium">
                            {summary.technical.latest.data.summary.strength}%
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span>Confidence:</span>
                          <span className="font-medium">
                            {Math.round(
                              (summary.technical.latest.qualityScore || 0) *
                                100,
                            )}
                            %
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">
                    No technical analysis available
                  </p>
                )}

                {/* Timeframes Breakdown */}
                {summary.technical?.timeframes && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <h5 className="font-semibold text-sm mb-3">Timeframes</h5>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        '1m',
                        '5m',
                        '15m',
                        '30m',
                        '1h',
                        '4h',
                        '1d',
                        '1w',
                        '1M',
                        '1Y',
                      ].map((timeframe) => {
                        const tf = summary.technical?.timeframes?.[timeframe];
                        const label =
                          timeframe === '1m'
                            ? '1 Minute'
                            : timeframe === '5m'
                              ? '5 Minutes'
                              : timeframe === '15m'
                                ? '15 Minutes'
                                : timeframe === '30m'
                                  ? '30 Minutes'
                                  : timeframe === '1h'
                                    ? '1 Hour'
                                    : timeframe === '4h'
                                      ? '4 Hours'
                                      : timeframe === '1d'
                                        ? '1 Day'
                                        : timeframe === '1w'
                                          ? '1 Week'
                                          : timeframe === '1M'
                                            ? '1 Month'
                                            : timeframe === '1Y'
                                              ? '1 Year'
                                              : timeframe;
                        const summaryData = tf?.result?.data?.summary;
                        return (
                          <div
                            key={timeframe}
                            className="flex flex-col p-2 bg-gray-50 rounded"
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-medium capitalize">
                                {label}
                              </span>
                              <div className="flex items-center space-x-1">
                                {summaryData?.trend && (
                                  <div className="flex items-center space-x-1">
                                    {getTrendIcon(summaryData.trend)}
                                    <span className="text-xs font-medium capitalize">
                                      {summaryData.trend}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                            {/* Observations */}
                            {summaryData?.observations &&
                              summaryData.observations.length > 0 && (
                                <ul className="list-disc list-inside text-xs text-gray-600 mt-1">
                                  {summaryData.observations.map(
                                    (obs: string, idx: number) => (
                                      <li key={idx}>{obs}</li>
                                    ),
                                  )}
                                </ul>
                              )}
                            {/* Key Levels */}
                            {summaryData?.keyLevels &&
                              summaryData.keyLevels.length > 0 && (
                                <div className="text-xs text-gray-600 mt-1">
                                  <span className="font-medium">
                                    Key Levels:
                                  </span>
                                  <ul className="list-disc list-inside mt-1">
                                    {summaryData.keyLevels.map(
                                      (kl: any, idx: number) => {
                                        if (kl.price !== undefined) {
                                          return (
                                            <li key={idx}>
                                              {kl.type
                                                ? kl.type
                                                    .charAt(0)
                                                    .toUpperCase() +
                                                  kl.type.slice(1)
                                                : 'Level'}
                                              :{' '}
                                              <span className="font-mono">
                                                {kl.price}
                                              </span>
                                              {kl.probability !== undefined
                                                ? ` (${kl.probability}%`
                                                : ''}
                                              {kl.timeframe
                                                ? `, ${kl.timeframe}`
                                                : ''}
                                              {kl.probability !== undefined
                                                ? ')'
                                                : ''}
                                            </li>
                                          );
                                        }
                                        return (
                                          <li key={idx}>
                                            {JSON.stringify(kl)}
                                          </li>
                                        );
                                      },
                                    )}
                                  </ul>
                                </div>
                              )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Fundamental Analysis */}
              <div className="bg-white p-6 rounded-lg border">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    <DollarSign className="h-5 w-5 text-green-600" />
                    <h4 className="text-lg font-semibold">
                      Fundamental Analysis
                    </h4>
                  </div>
                  {getStatusIcon(
                    summary.fundamental?.status || 'not_available',
                  )}
                </div>

                {summary.fundamental?.latest ? (
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span>Status:</span>
                      <span className="font-medium capitalize">
                        {summary.fundamental.status}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Last Analysis:</span>
                      <span>
                        {summary.fundamental.lastAnalysisDate
                          ? formatDate(summary.fundamental.lastAnalysisDate)
                          : 'N/A'}
                      </span>
                    </div>
                    {/* AI Enhancement Section */}
                    {summary.fundamental.latest.data?.aiEnhancement && (
                      <>
                        <div className="mt-2">
                          <h5 className="font-semibold text-xs mb-1">
                            AI Valuation Assessment
                          </h5>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div>Fair Value:</div>
                            <div>
                              {summary.fundamental.latest.data.aiEnhancement
                                .valuationAssessment?.fairValue ?? 'N/A'}
                            </div>
                            <div>Confidence:</div>
                            <div>
                              {summary.fundamental.latest.data.aiEnhancement
                                .valuationAssessment?.confidence ?? 'N/A'}
                              %
                            </div>
                            <div>Methodology:</div>
                            <div>
                              {summary.fundamental.latest.data.aiEnhancement
                                .valuationAssessment?.methodology ?? 'N/A'}
                            </div>
                            <div>Upside Potential:</div>
                            <div>
                              {summary.fundamental.latest.data.aiEnhancement
                                .valuationAssessment?.upsidePotential ?? 'N/A'}
                              %
                            </div>
                          </div>
                          <div className="text-xs mt-1">
                            <span className="font-medium">Risk Factors:</span>{' '}
                            {summary.fundamental.latest.data.aiEnhancement.valuationAssessment?.riskFactors?.join(
                              ', ',
                            ) || 'N/A'}
                          </div>
                        </div>
                        <div className="mt-2">
                          <h5 className="font-semibold text-xs mb-1">
                            AI Growth Analysis
                          </h5>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div>Revenue Growth Outlook:</div>
                            <div>
                              {summary.fundamental.latest.data.aiEnhancement
                                .growthAnalysis?.revenueGrowthOutlook ?? 'N/A'}
                            </div>
                            <div>Earnings Growth Outlook:</div>
                            <div>
                              {summary.fundamental.latest.data.aiEnhancement
                                .growthAnalysis?.earningsGrowthOutlook ?? 'N/A'}
                            </div>
                            <div>Growth Confidence:</div>
                            <div>
                              {summary.fundamental.latest.data.aiEnhancement
                                .growthAnalysis?.growthConfidence ?? 'N/A'}
                              %
                            </div>
                          </div>
                          <div className="text-xs mt-1">
                            <span className="font-medium">Key Drivers:</span>{' '}
                            {summary.fundamental.latest.data.aiEnhancement.growthAnalysis?.keyGrowthDrivers?.join(
                              ', ',
                            ) || 'N/A'}
                          </div>
                          <div className="text-xs mt-1">
                            <span className="font-medium">Growth Risks:</span>{' '}
                            {summary.fundamental.latest.data.aiEnhancement.growthAnalysis?.growthRisks?.join(
                              ', ',
                            ) || 'N/A'}
                          </div>
                        </div>
                        <div className="mt-2">
                          <h5 className="font-semibold text-xs mb-1">
                            AI Competitive Position
                          </h5>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div>Market Position:</div>
                            <div>
                              {summary.fundamental.latest.data.aiEnhancement
                                .competitivePosition?.marketPosition ?? 'N/A'}
                            </div>
                            <div>Moat Strength:</div>
                            <div>
                              {summary.fundamental.latest.data.aiEnhancement
                                .competitivePosition?.moatStrength ?? 'N/A'}
                            </div>
                          </div>
                          <div className="text-xs mt-1">
                            <span className="font-medium">Advantages:</span>{' '}
                            {summary.fundamental.latest.data.aiEnhancement.competitivePosition?.competitiveAdvantages?.join(
                              ', ',
                            ) || 'N/A'}
                          </div>
                          <div className="text-xs mt-1">
                            <span className="font-medium">Threats:</span>{' '}
                            {summary.fundamental.latest.data.aiEnhancement.competitivePosition?.competitiveThreats?.join(
                              ', ',
                            ) || 'N/A'}
                          </div>
                        </div>
                        <div className="mt-2">
                          <h5 className="font-semibold text-xs mb-1">
                            AI Investment Thesis
                          </h5>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div>Bull Case:</div>
                            <div>
                              {summary.fundamental.latest.data.aiEnhancement
                                .investmentThesis?.bullCase ?? 'N/A'}
                            </div>
                            <div>Bear Case:</div>
                            <div>
                              {summary.fundamental.latest.data.aiEnhancement
                                .investmentThesis?.bearCase ?? 'N/A'}
                            </div>
                            <div>Neutral Case:</div>
                            <div>
                              {summary.fundamental.latest.data.aiEnhancement
                                .investmentThesis?.neutralCase ?? 'N/A'}
                            </div>
                            <div>Time Horizon:</div>
                            <div>
                              {summary.fundamental.latest.data.aiEnhancement
                                .investmentThesis?.timeHorizon ?? 'N/A'}
                            </div>
                          </div>
                          <div className="text-xs mt-1">
                            <span className="font-medium">Key Catalysts:</span>{' '}
                            {summary.fundamental.latest.data.aiEnhancement.investmentThesis?.keyCatalysts?.join(
                              ', ',
                            ) || 'N/A'}
                          </div>
                        </div>
                      </>
                    )}
                    {/* Summary Section */}
                    {summary.fundamental.latest.data?.summary && (
                      <div className="mt-2">
                        <h5 className="font-semibold text-xs mb-1">Summary</h5>
                        <div>
                          Overall Score:{' '}
                          {summary.fundamental.latest.data.summary
                            .overallScore ?? 'N/A'}
                        </div>
                        <div>
                          Recommendation:{' '}
                          {summary.fundamental.latest.data.summary
                            .recommendation ?? 'N/A'}
                        </div>
                        <div>
                          Valuation:{' '}
                          {summary.fundamental.latest.data.summary
                            .valuationAssessment ?? 'N/A'}
                        </div>
                      </div>
                    )}
                    {/* Key Ratios Section */}
                    {summary.fundamental.latest.data?.ratios && (
                      <div className="mt-2">
                        <h5 className="font-semibold text-xs mb-1">
                          Key Ratios
                        </h5>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div>P/E Ratio:</div>
                          <div>
                            {summary.fundamental.latest.data.ratios.peRatio ??
                              'N/A'}
                          </div>
                          <div>Price/Book:</div>
                          <div>
                            {summary.fundamental.latest.data.ratios
                              .priceToBookRatio ?? 'N/A'}
                          </div>
                          <div>Debt/Equity:</div>
                          <div>
                            {summary.fundamental.latest.data.ratios
                              .debtToEquityRatio ?? 'N/A'}
                          </div>
                          <div>Return on Equity:</div>
                          <div>
                            {summary.fundamental.latest.data.ratios
                              .returnOnEquity ?? 'N/A'}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">
                    No fundamental analysis available
                  </p>
                )}
              </div>

              {/* Article Mentions */}
              <div className="bg-white p-6 rounded-lg border">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    <Users className="h-5 w-5 text-orange-600" />
                    <h4 className="text-lg font-semibold">Article Mentions</h4>
                  </div>
                  {summary.articleMentions ? (
                    <span className="text-sm font-medium text-gray-600">
                      {summary.articleMentions.totalMentions} mentions
                    </span>
                  ) : (
                    <Info className="h-4 w-4 text-gray-500" />
                  )}
                </div>

                {summary.articleMentions ? (
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span>Total Mentions:</span>
                      <span className="font-medium">
                        {summary.articleMentions.totalMentions}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Average Sentiment:</span>
                      <div className="flex items-center space-x-1">
                        {getTrendIcon(summary.articleMentions.averageSentiment)}
                        <span
                          className="font-medium capitalize"
                          style={{
                            color: getSentimentColor(
                              summary.articleMentions.averageSentiment,
                            ),
                          }}
                        >
                          {summary.articleMentions.averageSentiment}
                        </span>
                      </div>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Average Confidence:</span>
                      <span className="font-medium">
                        {summary.articleMentions.averageConfidence}%
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Recent Trend:</span>
                      <span className="font-medium capitalize">
                        {summary.articleMentions.recentTrend}
                      </span>
                    </div>
                    {summary.articleMentions.lastMentionDate && (
                      <div className="flex justify-between text-sm">
                        <span>Last Mention:</span>
                        <span>
                          {formatDate(summary.articleMentions.lastMentionDate)}
                        </span>
                      </div>
                    )}
                    {articleMentions.length > 0 && (
                      <button
                        onClick={() =>
                          setShowArticleMentions(!showArticleMentions)
                        }
                        className="w-full mt-3 px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100"
                      >
                        {showArticleMentions ? 'Hide' : 'Show'} Recent Mentions
                      </button>
                    )}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">
                    No article mentions available
                  </p>
                )}
              </div>
            </div>

            {/* Article Mentions List */}
            {showArticleMentions && articleMentions.length > 0 && (
              <div className="bg-white p-6 rounded-lg border">
                <h4 className="text-lg font-semibold mb-4">
                  Recent Article Mentions
                </h4>
                <div className="space-y-3">
                  {articleMentions.map((mention, index) => (
                    <div
                      key={index}
                      className="border-b border-gray-100 pb-3 last:border-b-0"
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h5 className="font-medium text-sm text-gray-900">
                            {mention.articleTitle}
                          </h5>
                          <p className="text-xs text-gray-500 mt-1">
                            {mention.context}
                          </p>
                          <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                            <span>{formatDate(mention.publishedAt)}</span>
                            <span>Confidence: {mention.confidence}%</span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2 ml-4">
                          <span
                            className="px-2 py-1 rounded-full text-xs font-medium"
                            style={{
                              backgroundColor:
                                getSentimentColor(mention.sentiment) + '20',
                              color: getSentimentColor(mention.sentiment),
                            }}
                          >
                            {mention.sentiment}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Analysis History */}
            <div className="bg-white p-6 rounded-lg border">
              <h4 className="text-lg font-semibold mb-4">Analysis History</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="font-medium">Technical:</span>{' '}
                  {summary.technical?.lastAnalysisDate
                    ? formatDate(summary.technical.lastAnalysisDate)
                    : 'Never'}
                </div>
                <div>
                  <span className="font-medium">Fundamental:</span>{' '}
                  {summary.fundamental?.lastAnalysisDate
                    ? formatDate(summary.fundamental.lastAnalysisDate)
                    : 'Never'}
                </div>
                <div>
                  <span className="font-medium">Sentiment:</span>{' '}
                  {articleMentions.length > 0
                    ? formatDate(articleMentions[0].publishedAt)
                    : 'Never'}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="p-6 bg-gray-50 rounded-lg">
            <p className="text-gray-600">No analysis data available</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TickerAnalysisSummaryModal;
